import { Children, cloneElement, isValidElement, useContext, type HTMLAttributes, type Ref } from "react";

import { cn } from "../../lib/utils";
import { Button, ButtonGroup, type ButtonProps } from "../../atoms/Button";
import { ItemSizeContext } from "./ItemContext";

export interface ItemActionsProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
}

// Row of row-level actions -- children must be `Button` (or `Button.Group`) so an Item's actions
// stay visually/behaviorally consistent with every other action surface in the system. When the
// owning Item is `size="sm"`, Button children scale down to `size="sm"` too (unless a child
// already sets its own `size`), so a compact row doesn't end up with oversized actions.
export const ItemActions = ({ className, ref, children, ...props }: ItemActionsProps) => {
  const itemSize = useContext(ItemSizeContext);

  if (process.env.NODE_ENV !== "production") {
    Children.forEach(children, (child) => {
      if (child && typeof child === "object" && "type" in child && child.type !== Button && child.type !== ButtonGroup) {
        console.error("Item.Actions only accepts Button or Button.Group children.");
      }
    });
  }

  const resolvedChildren =
    itemSize === "sm"
      ? Children.map(children, (child) =>
          isValidElement<ButtonProps>(child) && child.type === Button && child.props.size === undefined
            ? cloneElement(child, { size: "sm" })
            : child,
        )
      : children;

  return (
    <div ref={ref} data-slot="item-actions" className={cn("flex items-center gap-2", className)} {...props}>
      {resolvedChildren}
    </div>
  );
};
